import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import BidQuestionScoring from '../../comps/BidQuestionScoring'
import BidQuestionsLeftPanel from './BidQuestionsLeftPanel'
import TabHeader from '../../comps/TabHeader'
import FancyProgressArc from '../../comps/FancyProgressArc'
import BatchScoreButton from '../../comps/buttons/BatchScoreButton'
import BatchImproveButton from '../../comps/buttons/BatchImproveButton'
import { FS_MICRO, FS_SM, FS_XS, ICON_LG, ICON_SM } from '../../common/theme'
import { questionAnchorId, type Project, type Question } from '../../types'
import { QUESTION_PAGE_SIZE } from '../../comps/BidQuestion'

// Compact paginator — same shape as the one in BidQuestionsTab so the
// scoring tab matches that layout.
function CompactPaginator({ page, totalPages, total, onPage }: {
  page: number; totalPages: number; total: number; onPage: (p: number) => void
}) {
  if (total === 0) return null
  const start = (page - 1) * QUESTION_PAGE_SIZE + 1
  const end = Math.min(page * QUESTION_PAGE_SIZE, total)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6, whiteSpace: 'nowrap', mr: 0.5 }}>
        {start}–{end} of {total}
      </Typography>
      <IconButton size="small" disabled={page <= 1} onClick={() => onPage(1)} sx={{ p: 0.25 }}>
        <FirstPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <IconButton size="small" disabled={page <= 1} onClick={() => onPage(page - 1)} sx={{ p: 0.25 }}>
        <ChevronLeftIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <Typography variant="caption" sx={{ fontSize: FS_SM, mx: 0.25, whiteSpace: 'nowrap' }}>
        {page}/{totalPages}
      </Typography>
      <IconButton size="small" disabled={page >= totalPages} onClick={() => onPage(page + 1)} sx={{ p: 0.25 }}>
        <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <IconButton size="small" disabled={page >= totalPages} onClick={() => onPage(totalPages)} sx={{ p: 0.25 }}>
        <LastPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
    </Box>
  )
}

const ALL_KEY = '__all__'
const UNSECTIONED_KEY = '__unsectioned__'

interface BidScoringTabProps {
  project: Project
  allFilteredQuestions: Question[]
  pagedQuestions: Question[]
  totalQuestions: number
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  stateFilter?: string
  sectionFilter?: string
  onSectionChange?: (s: string | undefined) => void
  onBatchProcess?: (questions: Question[], options?: { genscore?: boolean; genimprovements?: boolean; genwintheme?: boolean }) => void
  onQuestionChange?: (q: Question) => void
}

// Scoring top-level tab. Left column: question navigator. Right
// column: overall bid score header + per-question scoring rows. Win
// Themes lives in its own sibling tab (BidWinThemesTab) so the user
// reaches it directly from the project navigation rather than via a
// secondary sub-tab.
export default function BidScoreTab({
  project, allFilteredQuestions, pagedQuestions, totalQuestions, currentPage, totalPages, onPageChange,
  stateFilter, sectionFilter, onSectionChange,
  onBatchProcess, onQuestionChange,
}: BidScoringTabProps) {
  console.log('BidScoreTab render');

  const overallScore = (project as any).projectbideval?.scoreint ?? 0

  // ── Section tabs (mirrors BidQuestionsTab) ───────────────────────────
  const { sectionList, hasUnsectioned } = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    let unsectioned = false
    for (const q of project.questions ?? []) {
      const sec = (q as any).section as string | null | undefined
      if (sec) {
        if (!seen.has(sec)) { seen.add(sec); list.push(sec) }
      } else {
        unsectioned = true
      }
    }
    return { sectionList: list, hasUnsectioned: unsectioned }
  }, [project.questions])

  const showSectionTabs = sectionList.length > 0

  const activeKey =
    sectionFilter === undefined ? ALL_KEY :
      sectionFilter === '' ? UNSECTIONED_KEY :
        sectionFilter

  const sectionTabs = [
    { key: ALL_KEY, label: 'All' },
    ...sectionList.map(s => ({ key: s, label: s })),
    ...(hasUnsectioned ? [{ key: UNSECTIONED_KEY, label: 'Unsectioned' }] : []),
  ]

  const handleSectionTabChange = (key: string) => {
    if (!onSectionChange) return
    if (key === ALL_KEY) onSectionChange(undefined)
    else if (key === UNSECTIONED_KEY) onSectionChange('')
    else onSectionChange(key)
  }

  // ── Left panel state ──────────────────────────────────────────────────
  const [leftPanelWidth, setLeftPanelWidth] = useState(280)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [selectedShas, setSelectedShas] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(280)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const maxW = (containerRef.current?.clientWidth ?? window.innerWidth) - 200
      setLeftPanelWidth(Math.max(180, Math.min(maxW, startWidthRef.current + delta)))
    }
    const onUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = leftPanelWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  // Scroll the first question of the new page into view when the user pages
  // through the list. Skip the initial mount; defer to next frame so the new
  // rows have rendered before we look up their anchor ids.
  const isInitialPageRef = useRef(true)
  useEffect(() => {
    if (isInitialPageRef.current) {
      isInitialPageRef.current = false
      return
    }
    const firstSha = (pagedQuestions[0] as any)?.questionsha
    if (!firstSha) return
    const raf = requestAnimationFrame(() => {
      document.getElementById(questionAnchorId(firstSha))?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
    return () => cancelAnimationFrame(raf)
  }, [currentPage]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Batch actions ─────────────────────────────────────────────────────
  const batchSubject = (): Question[] => {
    // Use selected questions when any are selected, otherwise the whole
    // filtered list (matches the angularjs `pm.selectmultiplebool` flow).
    if (selectedShas.size > 0) {
      return (project.questions ?? []).filter((q: any) => selectedShas.has(q.questionsha))
    }
    return allFilteredQuestions
  }

  const handleBatchScore = () => {
    if (!onBatchProcess) return
    onBatchProcess(batchSubject(), { genscore: true })
  }

  const handleBatchImprove = () => {
    if (!onBatchProcess) return
    onBatchProcess(batchSubject(), { genimprovements: true })
  }

  return (
    <Box
      data-testid="bs_root"
      ref={containerRef}
      sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}
    >

      {/* Left panel - question navigator */}
      {!leftCollapsed && (
        <Box sx={{
          flex: `0 0 ${leftPanelWidth}px`, minWidth: 0,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <BidQuestionsLeftPanel
            allQuestions={allFilteredQuestions}
            page={currentPage}
            onPage={onPageChange}
            onCollapse={() => setLeftCollapsed(true)}
            selectedShas={selectedShas}
            onSelectionChange={setSelectedShas}
            onQuestionChange={onQuestionChange}
            onBatchProcess={onBatchProcess ? (qs) => onBatchProcess(qs) : undefined}
          />
        </Box>
      )}

      {/* Resize handle / re-expand strip */}
      {leftCollapsed ? (
        <Box sx={{
          width: 18, flexShrink: 0,
          borderRight: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          pt: 0.5, bgcolor: 'action.hover',
        }}>
          <Tooltip title="Show questions panel" placement="right">
            <IconButton
              data-testid="bs_leftpanel_expand_btn"
              size="small"
              onClick={() => setLeftCollapsed(false)}
              sx={{ p: 0.25 }}
            >
              <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: 7, flexShrink: 0,
            cursor: 'col-resize',
            borderLeft: 1, borderRight: 1, borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
        </Box>
      )}

      {/* Right side */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Overall bid score header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 1.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7, fontSize: FS_XS }}>
            Overall Bid Score
          </Typography>
          <FancyProgressArc value={overallScore} zerona scale={0.85} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.55, fontStyle: 'italic', mr: 1 }}>
            Estimated scores are for guidance only
          </Typography>
          
          <BatchScoreButton
            testId="bs_eval_batch_btn"
            count={allFilteredQuestions.length}
            selectedCount={selectedShas.size}
            onClick={handleBatchScore}
          />
          <BatchImproveButton
            testId="bs_improve_batch_btn"
            count={allFilteredQuestions.length}
            selectedCount={selectedShas.size}
            onClick={handleBatchImprove}
          />
        </Box>

        {/* Section tabs + paginator (same pattern as questions tab) */}
        {showSectionTabs ? (
          <TabHeader
            tabs={sectionTabs}
            active={activeKey}
            onChange={handleSectionTabChange}
            endSlot={
              <CompactPaginator
                page={currentPage}
                totalPages={totalPages}
                total={totalQuestions}
                onPage={onPageChange}
              />
            }
            testIdPrefix="bidscoringsection"
          />
        ) : (
          totalQuestions > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              px: 1, py: 0.5,
              borderBottom: 1, borderColor: 'divider',
              flexShrink: 0,
            }}>
              <CompactPaginator
                page={currentPage}
                totalPages={totalPages}
                total={totalQuestions}
                onPage={onPageChange}
              />
            </Box>
          )
        )}

        {/* Question list (per-question scoring rows) */}
        <Box data-testid="bs_list" sx={{ flex: 1, overflowY: 'auto' }}>
          {pagedQuestions.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.45 }}>
                {stateFilter ? 'No questions match this filter.' : 'No questions found.'}
              </Typography>
            </Box>
          ) : (
            pagedQuestions.map((q, i) => (
              <BidQuestionScoring
                key={(q as any).questionsha ?? i}
                question={q}
                project={project}
                index={(currentPage - 1) * QUESTION_PAGE_SIZE + i}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  )
}
